import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Plus, Trash2, Save, Loader2, Info } from "lucide-react";
import api, { getUser } from "@/lib/api";

/**
 * Bloco do Plano de Visitas de Acompanhamento.
 *
 * Editado pelo Gestor (ou admin) — segue anexo ao Plano de Cuidados na
 * exportação para a app de gestão operacional (18/02/2026 requirement).
 *
 * Só aparece quando o Plano de Cuidados existe (para não confundir
 * antes de haver actividades definidas).
 */
export default function FollowUpVisitPlanBlock({ lead, onChange }) {
  const user = getUser();
  const canEdit = user?.role === "admin" || user?.role === "gestor";
  const carePlan = lead?.technical?.care_plan || {};
  const hasCarePlan = Array.isArray(carePlan.activities) &&
                       carePlan.activities.some((a) => a?.activity);
  const [plan, setPlan] = useState(() => lead?.follow_up_visit_plan || { cadence: "", visits: [], notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPlan(lead?.follow_up_visit_plan || { cadence: "", visits: [], notes: "" });
  }, [lead?.follow_up_visit_plan]);

  if (!hasCarePlan) return null;

  const addVisit = () => setPlan({
    ...plan,
    visits: [...(plan.visits || []), {
      id: `v-${Date.now()}`,
      scheduled_for: "",
      purpose: "",
      notes: "",
      status: "planned",
    }],
  });
  const setVisit = (i, patch) => {
    const arr = [...(plan.visits || [])];
    arr[i] = { ...arr[i], ...patch };
    setPlan({ ...plan, visits: arr });
  };
  const rmVisit = (i) => setPlan({
    ...plan,
    visits: (plan.visits || []).filter((_, idx) => idx !== i),
  });

  const save = async () => {
    for (const v of plan.visits || []) {
      if (!v.scheduled_for || !v.purpose) {
        toast.error("Cada visita precisa de data e propósito.");
        return;
      }
    }
    setSaving(true);
    try {
      await api.put(`/leads/${lead.id}/visit-plan`, {
        cadence: plan.cadence || null,
        notes: plan.notes || null,
        visits: (plan.visits || []).map((v) => ({
          id: v.id, scheduled_for: v.scheduled_for, purpose: v.purpose,
          notes: v.notes || null, status: v.status || "planned",
          assignee_id: v.assignee_id || null, assignee_name: v.assignee_name || null,
          completed_at: v.completed_at || null, completed_by_name: v.completed_by_name || null,
        })),
      });
      toast.success("Plano de Visitas guardado");
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a guardar");
    } finally { setSaving(false); }
  };

  const inp = "bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary/50";

  return (
    <div className="bg-card border border-border rounded-2xl p-6 lg:p-8" data-testid="visit-plan-block">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">
            Segue com o Plano de Cuidados
          </div>
          <h3 className="font-ui text-lg inline-flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-foreground" />
            Plano de Visitas de Acompanhamento
          </h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Cadência + visitas planeadas pelo Gestor. Vai anexo ao ficheiro JSON exportado para a app de gestão operacional.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={save}
            disabled={saving}
            data-testid="visit-plan-save-btn"
            className="inline-flex items-center gap-2 text-sm bg-foreground hover:bg-slate-800 text-background rounded-full px-5 py-2.5 font-semibold disabled:opacity-40 flex-shrink-0"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="md:col-span-1">
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold block mb-1">
            Cadência
          </label>
          <select
            className={`${inp} w-full`}
            value={plan.cadence || ""}
            disabled={!canEdit}
            onChange={(e) => setPlan({ ...plan, cadence: e.target.value })}
            data-testid="visit-plan-cadence"
          >
            <option value="">— sem cadência fixa —</option>
            <option value="semanal">Semanal</option>
            <option value="quinzenal">Quinzenal</option>
            <option value="mensal">Mensal</option>
            <option value="trimestral">Trimestral</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold block mb-1">
            Notas gerais
          </label>
          <input
            className={`${inp} w-full`}
            value={plan.notes || ""}
            disabled={!canEdit}
            onChange={(e) => setPlan({ ...plan, notes: e.target.value })}
            placeholder="Ex.: alternar entre visita técnica e reavaliação clínica"
            data-testid="visit-plan-notes"
          />
        </div>
      </div>

      <div className="space-y-2">
        {(plan.visits || []).length === 0 && (
          <div className="text-xs text-muted-foreground bg-muted/40 border border-border rounded-lg p-4 flex items-start gap-2">
            <Info className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
            <div>
              Nenhuma visita planeada. {canEdit ? "Clique em 'Adicionar visita' para começar." : "O Gestor ainda não definiu visitas."}
            </div>
          </div>
        )}
        {(plan.visits || []).map((v, i) => (
          <div key={v.id || i} data-testid={`visit-row-${i}`}
                className="grid grid-cols-1 md:grid-cols-[220px_1fr_150px_40px] gap-2 items-start p-3 border border-border rounded-lg bg-muted/40">
            <input
              type="datetime-local"
              className={inp}
              value={v.scheduled_for || ""}
              disabled={!canEdit}
              onChange={(e) => setVisit(i, { scheduled_for: e.target.value })}
              data-testid={`visit-date-${i}`}
            />
            <input
              className={inp}
              placeholder="Propósito (ex.: Visita mensal)"
              value={v.purpose || ""}
              disabled={!canEdit}
              onChange={(e) => setVisit(i, { purpose: e.target.value })}
              data-testid={`visit-purpose-${i}`}
            />
            <select
              className={inp}
              value={v.status || "planned"}
              disabled={!canEdit}
              onChange={(e) => setVisit(i, { status: e.target.value })}
              data-testid={`visit-status-${i}`}
            >
              <option value="planned">Planeada</option>
              <option value="done">Realizada</option>
              <option value="cancelled">Cancelada</option>
            </select>
            {canEdit && (
              <button
                onClick={() => rmVisit(i)}
                className="text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger-bg))] rounded-lg p-2 justify-self-end"
                title="Remover visita"
                data-testid={`visit-remove-${i}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
        {canEdit && (
          <button
            onClick={addVisit}
            data-testid="visit-plan-add-btn"
            className="inline-flex items-center gap-1.5 text-xs bg-card hover:bg-muted border border-dashed border-primary text-foreground rounded-full px-3 py-1.5 font-semibold"
          >
            <Plus className="w-3 h-3" /> Adicionar visita
          </button>
        )}
      </div>

      {plan.defined_by_name && (
        <div className="text-[10px] text-muted-foreground mt-4 pt-3 border-t border-border">
          Definido por <b>{plan.defined_by_name}</b>
          {plan.defined_at && <> em {new Date(plan.defined_at).toLocaleString("pt-PT")}</>}
          {plan.updated_at && plan.updated_at !== plan.defined_at && (
            <> · última alteração {new Date(plan.updated_at).toLocaleString("pt-PT")}</>
          )}
        </div>
      )}
    </div>
  );
}
